import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import { useReactToPrint } from "react-to-print";
import useAuth from "../../useAuth";
import {
  FaPrint,
  FaIdCard,
  FaUserTie,
  FaSearch,
  FaSearchMinus,
  FaSearchPlus,
  FaUndo,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
  FaUsers,
  FaCheckCircle,
  FaBuilding,
  FaBriefcase,
  FaPhone,
  FaCalendarAlt,
} from "react-icons/fa";
import QRCode from "react-qr-code";
import { toast } from "react-toastify";

export default function GenerateEmployeeIdCard() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const { token } = useAuth();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${server}/employees`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data.data || [];
        setEmployees(data);
        if (data.length > 0) {
          setSelectedEmployee(data[0]);
        }
      } catch (error) {
        toast.error("Failed to load employees");
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchEmployees();
  }, [token]);

  // Photo adjustment states (Zoom, Position, Drag)
  const [photoScale, setPhotoScale] = useState(1);
  const [photoPos, setPhotoPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setPhotoScale(1);
    setPhotoPos({ x: 0, y: 0 });
  };

  // Drag handlers for photo positioning
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - photoPos.x, y: e.clientY - photoPos.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPhotoPos({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - photoPos.x,
        y: e.touches[0].clientY - photoPos.y,
      });
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    setPhotoPos({
      x: e.touches[0].clientX - dragStart.x,
      y: e.touches[0].clientY - dragStart.y,
    });
  };

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Employee_ID_Card_${selectedEmployee?.name || "Staff"}`,
    pageStyle: `
      @page {
        size: 360px 580px;
        margin: 0;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  // Extract unique departments
  const availableDepartments = Array.from(
    new Set(
      employees
        .map((e) => e.departmentName || e.department?.name)
        .filter(Boolean)
    )
  ).sort();

  const filteredEmployees = employees.filter((e) => {
    const deptName = e.departmentName || e.department?.name || "";
    const matchDepartment =
      selectedDepartment === "ALL" || String(deptName) === String(selectedDepartment);
    const matchSearch =
      !searchTerm.trim() ||
      e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.employeeCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.designation?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchDepartment && matchSearch;
  });

  const handleNextEmployee = () => {
    if (!filteredEmployees.length) return;
    const currentIndex = filteredEmployees.findIndex((e) => e.id === selectedEmployee?.id);
    const nextIndex = (currentIndex + 1) % filteredEmployees.length;
    handleEmployeeSelect(filteredEmployees[nextIndex]);
  };

  const handlePrevEmployee = () => {
    if (!filteredEmployees.length) return;
    const currentIndex = filteredEmployees.findIndex((e) => e.id === selectedEmployee?.id);
    const prevIndex = (currentIndex - 1 + filteredEmployees.length) % filteredEmployees.length;
    handleEmployeeSelect(filteredEmployees[prevIndex]);
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Header Bar */}
      <div className="print:hidden bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaIdCard size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
              Employee & Staff ID Card Generator
            </h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">
              Select a staff member to design and print their official Employee ID Badge
            </p>
          </div>
        </div>

        {selectedEmployee && (
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5 shrink-0"
            >
              <FaPrint size={11} /> Print Official Employee ID
            </button>
          </div>
        )}
      </div>

      {/* Main 2-Column Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* LEFT COLUMN: Filters & Staff List */}
        <div className="print:hidden lg:col-span-5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs flex flex-col gap-4">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-800 pb-3">
            <div className="flex items-center gap-2">
              <FaFilter className="text-indigo-600 dark:text-indigo-400 text-xs" />
              <span className="text-xs font-black text-gray-900 dark:text-white uppercase tracking-wider">
                Staff Directory & Filters
              </span>
            </div>
            <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 dark:bg-indigo-950 border border-indigo-200 dark:border-indigo-800 px-2 py-0.5 rounded-full">
              {filteredEmployees.length} Staff Members
            </span>
          </div>

          {/* Department Filter */}
          <div>
            <label className="block text-[10px] font-black uppercase text-gray-500 dark:text-slate-400 mb-1">
              Department
            </label>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="ALL">All Departments</option>
              {availableDepartments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          <div>
            <label className="block text-[10px] font-black uppercase text-gray-500 dark:text-slate-400 mb-1">
              Search Staff
            </label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-2.5 text-gray-400 text-xs" />
              <input
                type="text"
                placeholder="Search by Name, Code, Designation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-2 text-xs font-bold text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Staff Selection List */}
          <div className="flex flex-col">
            <div className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
              <FaUsers size={11} /> Select Employee:
            </div>

            {loading ? (
              <div className="py-12 text-center text-xs font-bold text-indigo-600">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                Loading employees...
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="py-12 text-center text-xs font-semibold text-gray-400 italic bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-gray-200 dark:border-slate-800">
                No employees match your filter.
              </div>
            ) : (
              <div className="overflow-y-auto space-y-1.5 pr-1 custom-scrollbar max-h-[460px]">
                {filteredEmployees.map((e) => {
                  const isSelected = selectedEmployee?.id === e.id;
                  const photo = e.profilePictureUrl || e.photoUrl;
                  return (
                    <button
                      key={e.id}
                      onClick={() => handleEmployeeSelect(e)}
                      className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left transition border ${
                        isSelected
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-xs"
                          : "bg-gray-50 dark:bg-slate-800/80 text-gray-800 dark:text-slate-100 border-gray-200 dark:border-slate-700/80 hover:border-indigo-300 dark:hover:border-indigo-500"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-slate-700 flex items-center justify-center overflow-hidden shrink-0 border border-white/40">
                          {photo ? (
                            <img
                              src={
                                photo.startsWith("http")
                                  ? photo
                                  : `${server}/${photo.replace(/^\/+/, "")}`
                              }
                              alt={e.name}
                              className="w-full h-full object-cover"
                              onError={(err) => {
                                err.target.onerror = null;
                                err.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <FaUserTie className="text-xs text-indigo-600 dark:text-indigo-400" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-black truncate leading-tight">
                            {e.name}
                          </div>
                          <div
                            className={`text-[10px] font-bold truncate ${
                              isSelected ? "text-indigo-100" : "text-gray-500 dark:text-slate-400"
                            }`}
                          >
                            {e.designation || "Staff"} | Code: {e.employeeCode || "N/A"}
                          </div>
                        </div>
                      </div>

                      {isSelected && (
                        <FaCheckCircle size={12} className="text-white shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: Card Preview & Toolbar */}
        <div className="lg:col-span-7 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs flex flex-col items-center min-h-[660px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 text-indigo-600 font-bold animate-pulse">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-3"></div>
              Loading Employee ID Card...
            </div>
          ) : !selectedEmployee ? (
            <div className="print:hidden w-full max-w-sm rounded-3xl border-2 border-dashed border-gray-300 dark:border-slate-800 p-12 flex flex-col items-center justify-center text-gray-400 opacity-60 my-auto">
              <FaIdCard className="text-6xl mb-4" />
              <p className="font-semibold text-center text-xs">Select an employee from the left panel</p>
            </div>
          ) : (
            <div
              id="id-card-print-area"
              className="w-full flex flex-col items-center pb-4"
            >
              {/* Photo & Navigation Control Toolbar */}
              <div className="print:hidden mb-4 bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700/80 p-3 rounded-2xl shadow-xs flex flex-wrap items-center justify-between gap-3 text-xs w-[360px]">
                {/* Switcher */}
                <div className="flex items-center border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden bg-white dark:bg-slate-800">
                  <button
                    type="button"
                    onClick={handlePrevEmployee}
                    disabled={filteredEmployees.length <= 1}
                    className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30 transition text-gray-700 dark:text-slate-200"
                    title="Previous Employee"
                  >
                    <FaChevronLeft size={10} />
                  </button>
                  <span className="px-2 text-[10px] font-black text-gray-600 dark:text-slate-300 border-x border-gray-200 dark:border-slate-700">
                    {filteredEmployees.findIndex((e) => e.id === selectedEmployee?.id) + 1} / {filteredEmployees.length}
                  </span>
                  <button
                    type="button"
                    onClick={handleNextEmployee}
                    disabled={filteredEmployees.length <= 1}
                    className="px-2 py-1 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30 transition text-gray-700 dark:text-slate-200"
                    title="Next Employee"
                  >
                    <FaChevronRight size={10} />
                  </button>
                </div>

                {/* Photo Zoom Controls */}
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPhotoScale((s) => Math.max(0.5, s - 0.1))}
                    className="p-1.5 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-indigo-100 hover:text-indigo-600 transition"
                    title="Zoom Out"
                  >
                    <FaSearchMinus size={11} />
                  </button>

                  <input
                    type="range"
                    min="0.5"
                    max="2.5"
                    step="0.05"
                    value={photoScale}
                    onChange={(e) => setPhotoScale(parseFloat(e.target.value))}
                    className="w-14 accent-indigo-600 cursor-pointer"
                    title="Zoom level"
                  />

                  <button
                    type="button"
                    onClick={() => setPhotoScale((s) => Math.min(2.5, s + 0.1))}
                    className="p-1.5 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-indigo-100 hover:text-indigo-600 transition"
                    title="Zoom In"
                  >
                    <FaSearchPlus size={11} />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setPhotoScale(1);
                      setPhotoPos({ x: 0, y: 0 });
                    }}
                    className="px-2 py-1 bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400 font-bold rounded-lg border border-rose-200 dark:border-rose-900 hover:bg-rose-100 transition flex items-center gap-1 text-[10px]"
                    title="Reset scale and position"
                  >
                    <FaUndo size={9} /> Reset
                  </button>
                </div>
              </div>

              {/* Physical Employee ID Card (Stylish Inclined Silk Gradient Header Design) */}
              <div
                ref={componentRef}
                className="w-[360px] h-[580px] bg-white rounded-[24px] shadow-2xl overflow-hidden flex flex-col shrink-0 relative z-0 border border-slate-200"
              >
                {/* Background Watermark Pattern */}
                <div className="absolute inset-0 pointer-events-none opacity-[0.015] flex items-center justify-center z-0">
                  <FaIdCard className="text-[340px] text-slate-900" />
                </div>

                {/* Top Inclined Silk Gradient Header Section (Vibrant Cobalt Royal Blue) */}
                <div
                  className="relative h-[165px] bg-gradient-to-br from-blue-700 via-indigo-700 to-blue-900 flex flex-col items-center justify-start pt-3.5 z-10 overflow-hidden"
                  style={{
                    clipPath: "polygon(0 0, 100% 0, 100% 86%, 0 100%)",
                  }}
                >
                  {/* Glossy Silk Sheen Effect & Cyan Glow Overlays */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/25 to-white/0 pointer-events-none transform -skew-x-12"></div>
                  <div className="absolute -top-12 -left-12 w-52 h-52 bg-cyan-400/30 rounded-full blur-2xl pointer-events-none"></div>
                  <div className="absolute -bottom-10 -right-10 w-44 h-44 bg-blue-400/30 rounded-full blur-xl pointer-events-none"></div>

                  {/* Lanyard Punch Hole */}
                  <div className="w-14 h-2.5 rounded-full border border-white/30 bg-blue-950/60 shadow-inner relative z-10 shrink-0"></div>

                  {/* School Logo & Title Block */}
                  <div className="flex flex-col items-center mt-2 relative z-10 leading-none">
                    <div className="bg-white p-1 px-3.5 rounded-xl shadow-md mb-1.5 border border-blue-200/50">
                      <img
                        src="/assets/images/logo.jpg"
                        alt="Logo"
                        className="h-[32px] w-auto object-contain"
                      />
                    </div>
                    <h2 className="text-white font-black text-[16px] uppercase tracking-[0.14em] drop-shadow-md">
                      Dee Novo School
                    </h2>
                    <div className="h-0.5 w-16 bg-gradient-to-r from-amber-300 via-amber-400 to-amber-300 rounded-full my-1 shadow-xs"></div>
                    <p className="text-[9px] font-black uppercase text-amber-300 tracking-[0.2em] drop-shadow-xs">
                      Official Faculty & Staff Badge
                    </p>
                  </div>
                </div>

                {/* Central Executive Content Area */}
                <div className="flex-1 -mt-[60px] flex flex-col items-center px-5 relative z-20">
                  {/* Interactive Profile Photo Overlapping the Inclined Header */}
                  <div
                    className="w-28 h-28 -mt-[52px] rounded-full border-4 border-white ring-4 ring-blue-500/30 shadow-2xl bg-blue-50 flex items-center justify-center overflow-hidden shrink-0 relative z-20 select-none cursor-grab active:cursor-grabbing"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleMouseUp}
                    title="Click and drag to position photo"
                  >
                    {(selectedEmployee.profilePictureUrl || selectedEmployee.photoUrl) ? (
                      <img
                        src={
                          (selectedEmployee.profilePictureUrl || selectedEmployee.photoUrl).startsWith("http")
                            ? (selectedEmployee.profilePictureUrl || selectedEmployee.photoUrl)
                            : `${server}/${(selectedEmployee.profilePictureUrl || selectedEmployee.photoUrl).replace(/^\/+/, "")}`
                        }
                        alt={selectedEmployee.name || "Staff"}
                        className="w-full h-full object-cover pointer-events-none"
                        style={{
                          transform: `translate(${photoPos.x}px, ${photoPos.y}px) scale(${photoScale})`,
                          transition: isDragging ? "none" : "transform 0.05s ease-out",
                        }}
                        onError={(err) => {
                          err.target.onerror = null;
                          err.target.style.display = "none";
                        }}
                      />
                    ) : null}
                    <FaUserTie className="text-5xl text-blue-400" />
                  </div>

                  {/* Staff Identity Block */}
                  <div className="w-full text-center mt-2.5">
                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight leading-tight px-1 truncate">
                      {selectedEmployee.name}
                    </h3>
                    <div className="mt-1 flex items-center justify-center gap-1.5">
                      <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 text-white border border-blue-400/30 px-4 py-1 rounded-full text-[11px] font-black uppercase tracking-widest shadow-md">
                        {selectedEmployee.designation || "STAFF MEMBER"}
                      </span>
                    </div>
                  </div>

                  {/* Clean Data Grid */}
                  <div className="w-full bg-slate-50/90 border border-slate-200/80 rounded-2xl p-4 mt-3 shadow-2xs space-y-2 text-xs">
                    <div className="flex justify-between items-center border-b border-slate-200/60 pb-1.5">
                      <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                        <FaIdCard className="text-blue-600 text-xs" /> Staff ID:
                      </span>
                      <span className="font-mono font-black text-blue-600 text-xs">
                        {selectedEmployee.employeeCode || "N/A"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center border-b border-slate-200/60 pb-1.5">
                      <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                        <FaBuilding className="text-blue-600 text-xs" /> Department:
                      </span>
                      <span className="font-bold text-slate-800 truncate max-w-[170px]">
                        {selectedEmployee.departmentName || selectedEmployee.department?.name || "School Staff"}
                      </span>
                    </div>

                    {selectedEmployee.mobileNo && (
                      <div className="flex justify-between items-center border-b border-slate-200/60 pb-1.5">
                        <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                          <FaPhone className="text-blue-600 text-xs" /> Mobile:
                        </span>
                        <span className="font-bold text-slate-800 font-mono">
                          {selectedEmployee.mobileNo}
                        </span>
                      </div>
                    )}

                    {selectedEmployee.joiningDate && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 font-extrabold uppercase tracking-wider text-[10px] flex items-center gap-1.5">
                          <FaCalendarAlt className="text-blue-600 text-xs" /> Date Joined:
                        </span>
                        <span className="font-bold text-slate-800">
                          {selectedEmployee.joiningDate}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Bar with Authorised Signatory & QR Code */}
                <div className="h-[80px] bg-slate-50 border-t border-slate-100 flex items-center justify-between px-6 py-2 relative z-10 mt-auto rounded-b-[24px]">
                  <div className="flex flex-col">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      AUTHORISED SIGNATORY
                    </span>
                    <span className="text-[10px] font-extrabold text-slate-700 mt-0.5 italic">
                      Dee Novo School Administration
                    </span>
                  </div>

                  <div className="bg-white p-1 rounded-xl border border-slate-200 shadow-2xs shrink-0">
                    <QRCode
                      value={selectedEmployee.employeeCode || "EMPLOYEE-ID"}
                      size={44}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
